import React, { useEffect, useState, useRef } from 'react';
import './DetectorStatus.css';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import TeamHeader from '../TeamHeader/TeamHeader';
import { FaMagnifyingGlass } from "react-icons/fa6";
import { useDetectorRelations } from './useDetectorRelations';

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap } from "leaflet";

// Assets
import redMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-red.png'
import yellowMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-yellow.png'
import greenMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-grey.png'
import googleMapsLogo from '../../backend/assets/google-map-logo.png'

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import { FaMapMarkerAlt, FaCircle } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import { BsExclamationSquare } from "react-icons/bs";
import { IoSearchSharp, IoClose } from "react-icons/io5";
import { MdFileDownload } from "react-icons/md";
import { PiPlugsFill } from "react-icons/pi";
import { AiOutlineAim } from "react-icons/ai";

interface FNNUsers {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

// Interfaces for Most Recent Files
export interface DetectorFile {
    filename: string;
    timestamp: string;
    filepath: string;
}

export type AllServerFilesObject = {
    [siteName: string]: DetectorFile[];
};

export interface NewServerFilesFormat {
    data: AllServerFilesObject;
    last_triggered: string;
}

interface HardwareNote {
    note_id: number;
    maintenanced_by: number;
    note_related_detector: string;
    note_date_and_time: string;
    note_description: string;
    note_parameters: any;
    images?: { id: number; image: string }[];
    files?: { id: number; file: string }[];
}

export const redIcon = new Icon({
    iconUrl: redMarker,
    iconSize: [50, 50]
});

export const yellowIcon = new Icon({
    iconUrl: yellowMarker,
    iconSize: [50, 50]
});

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50]
});

export const greenIcon = new Icon({
    iconUrl: greenMarker,
    iconSize: [50, 50]
});

const DetectorStatus = () => {
    const { fullDetectors, detectorCodeObj, subAssemblies, detectors, refreshDetectors, lastFetched } = useDetectorRelations();

    const [regions, setRegions] = useState<string[]>(["All"]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>(["All"]);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [testFilter, setTestFilter] = useState<"include" | "exclude">("include");
    const [selectedDetector, setSelectedDetector] = useState<typeof fullDetectors[0] | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("details");
    const [showFilePopup, setShowFilePopup] = useState(false);
    const [fnnUsers, setFnnUsers] = useState<FNNUsers[]>([]);
    const [hardwareNotes, setHardwareNotes] = useState<HardwareNote[]>([]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch FNN Users
    useEffect(() => {
        const fetchFNNUsers = async () => {
            try {
                const response = await fetch('/api/inventory/fnn-users/', {
                    credentials: "same-origin"
                });
                const data = await response.json();
                if (response.ok) {
                    setFnnUsers(data);
                } else {
                    console.error("Failed to load fnn users:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching fnn users:", error);
            }
        };

        fetchFNNUsers();
    }, []);

    const fetchHardwareNotes = async () => {
        try {
            const response = await fetch('/api/hardware_history/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                const parsedData = data.map((n: HardwareNote) => ({
                    ...n,
                    note_parameters: typeof n.note_parameters === 'string'
                        ? JSON.parse(n.note_parameters)
                        : n.note_parameters
                }));
                setHardwareNotes(parsedData);
            } else {
                console.error("Failed to fetch hardware notes:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching hardware notes:", error);
        }
    };

    useEffect(() => {
        fetchHardwareNotes();
    }, []);

    // Extract unique regions from detector codes
    useEffect(() => {
        if (!detectorCodeObj.length) return;

        const unique = Array.from(
            new Set(detectorCodeObj.map(obj => obj.reg).filter(Boolean))
        ).filter(region => region !== "Test"); // Exclude "Test" region

        setRegions(["All", ...unique]);
    }, [detectorCodeObj]);

    // --- Filtered detectors (Region + Status + Search + Test) ---
    const filteredDetectors = (() => {
        let baseData = fullDetectors;

        // ----- REGION FILTER -----
        if (!selectedRegions.includes("All")) {
            baseData = baseData.filter(det => {
                // If no code entry, not part of any region
                if (!det.code) return false;
                // Detector belongs to region if its reg is selected
                return selectedRegions.includes(det.code.reg);
            });
        }

        // ----- SEARCH FILTER -----
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();

            baseData = baseData.filter(d => {
                const mac = d.monitoring.mac?.toLowerCase() || "";
                const site = d.monitoring.status?.site_name?.toLowerCase() || "";
                const ip = d.monitoring.ip?.toLowerCase() || "";
                const receiver = d.code?.receiver?.toLowerCase() || "";
                const codeName = d.code?.name?.toLowerCase() || "";
                const code = d.code?.code?.toLowerCase() || "";

                return (
                    mac.includes(term) ||
                    site.includes(term) ||
                    ip.includes(term) ||
                    receiver.includes(term) ||
                    codeName.includes(term) ||
                    code.includes(term)
                );
            });
        }


        // ----- STATUS FILTER -----
        if (statusFilters.length > 0) {
            baseData = baseData.filter(d => {
                const status = d.monitoring.online ? "Online" : "Offline";
                return statusFilters.includes(status);
            });
        }

        // ----- TEST FILTER -----
        if (testFilter === "exclude") {
            baseData = baseData.filter(d => {
                // Check if this detector would be marked as "T"
                const now = new Date();
                if (!d.code) return false; // No code = "T" detector

                const expiration = d.code.expiration_date
                    ? new Date(d.code.expiration_date)
                    : null;

                const notExpired = !expiration || expiration > now;
                const displayCode = notExpired ? (d.code.code?.trim() || "U") : "T";

                return displayCode !== "T"; // Exclude "T" detectors
            });
        }

        return baseData;
    })();

    const getDetectorColor = (det: typeof fullDetectors[0]) => {
        if (!det.monitoring.online) return "#a6a6a6";
        const mem = parseFloat(det.monitoring.status.memory);
        if (mem >= 85) return "#FF4C4C";
        if (mem >= 80 && mem < 85) return "#FFD93D";
        return "#20bf55";
    };

    const darkenColor = (hex: string, amount = 30): string => {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);

        r = Math.max(0, r - amount);
        g = Math.max(0, g - amount);
        b = Math.max(0, b - amount);

        const toHex = (n: number) => n.toString(16).padStart(2, "0");
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const getColorForMemory = (count: number) => {
        if (count > 85) return "red";
        if (count > 80 && count <= 85) return "#d5b60a";
        if (count <= 80) return "#008000";
    };

    const getColorForGPSFix = (isOn: boolean) => {
        if (isOn === false) return "red";
        if (isOn === true) return "#008000";
    };

    const getColorForMarker = (isOn: boolean, memoryValue: string | number) => {
        if (typeof memoryValue === "string" && /[a-zA-Z]/.test(memoryValue)) {
            return redIcon;
        }

        const numericMemory = Number(memoryValue);

        if (isOn === false) return BwIcon;
        if (numericMemory > 85 && isOn === true) return redIcon;
        if (numericMemory > 80 && numericMemory <= 85 && isOn === true) return yellowIcon;
        if (numericMemory <= 80 && isOn === true) return greenIcon;

        return redIcon;
    };

    const handleSelectDetector = (detector: typeof fullDetectors[0]) => {
        setSelectedDetector(detector);

        // Reset scope display when switching detectors
        setCommandResults(prev => ({ ...prev, SCOPE: '' }));

        if (activeTab === "config") {
            if (detector.monitoring.online) {
                if (detector.monitoring.ip) {
                    displayCommand(detector.monitoring.ip, "GET_LOC");
                    displayCommand(detector.monitoring.ip, "GET_DAC");
                    displayCommand(detector.monitoring.ip, "GET_THR");
                    displayCommand(detector.monitoring.ip, "GET_MEM");
                }
            } else {
                setActiveTab("details");
            }
        }
    };


    const getFnnUserName = (userId: string | number | null | undefined) => {
        if (!userId || !fnnUsers?.length) return "—";
        const user = fnnUsers.find((u) => String(u.id) === String(userId));
        if (!user) return "—";
        return `${user.first_name} ${user.last_name}`.trim() || user.username || "—";
    };

    // --------------------------------- Build hardware version info for selected detector -----------------------------------
    const detectorVersionInfo = (() => {
        if (!selectedDetector?.built) return null;

        const builtDet = selectedDetector.built;
        const detBom = selectedDetector.detectorBom;
        const builtSubs = selectedDetector.builtSubAssemblies || [];

        if (!detBom) return null;

        const subassemblyDisplay = builtDet.subassemblies.map(subId => {
            const builtSub = builtSubs.find(s => s.id === subId);
            let subName = "Unknown";

            if (builtSub) {
                const relatedSub = subAssemblies.find(sa => sa.id === builtSub.subassembly);
                if (relatedSub) subName = relatedSub.name;
            } else {
                const code = subId.split("-")[0];
                const relatedSub = subAssemblies.find(sa =>
                    sa.name.toLowerCase().includes(code.toLowerCase())
                );
                if (relatedSub) subName = relatedSub.name;
            }

            return `${subId}: ${subName}`;
        });

        return {
            built_at: builtDet.built_at,
            assembled_by: builtDet.assembled_by,
            subassemblies: subassemblyDisplay,
        };
    })();

    function showInfoTooltip(message: string, extraClassName?: string) {
        return (
            <div className="tooltip-wrapper">
                <FaCircleInfo />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {message}
                </div>
            </div>
        );
    }

    function Tooltip({
        message,
        showTooltip,
        children,
        extraClassName = "",
    }: {
        message: string;
        showTooltip: boolean;
        children: React.ReactNode;
        extraClassName?: string;
    }) {
        return (
            <div className="tooltip-button-wrapper">
                {children}
                {showTooltip && (
                    <div className={`tooltip-text show ${extraClassName}`}>
                        {message}
                    </div>
                )}
            </div>
        );
    }


    // ------------------------------------- Handles reference between dashboard and map -------------------------------------
    const mapRef = useRef<LeafletMap | null>(null);
    const markerRefs = useRef<{ [key: string]: any }>({});
    const openPopup = (mac: string) => {
        const marker = markerRefs.current[mac];
        if (marker) {
            marker.openPopup();
            const { lat, lng } = marker.getLatLng();
            const offsetLat = lat + 1;
            mapRef.current?.flyTo([offsetLat, lng], 7, { duration: 1.2 });
        }
    };

    // ----------------------------------------- Handling pagination inside the grid -----------------------------------------
    const [currentPage, setCurrentPage] = useState(1);
    const [detectorsPerPage, setDetectorsPerPage] = useState(0);
    const gridRef = useRef<HTMLDivElement | null>(null);

    // 1️⃣ Preprocess and sort ALL detectors before pagination
    const processedAndSortedDetectors = filteredDetectors
        .map(det => {
            const now = new Date();
            let displayCode = "T";

            if (det.code) {
                const expiration = det.code.expiration_date
                    ? new Date(det.code.expiration_date)
                    : null;

                const notExpired = !expiration || expiration > now;
                displayCode = notExpired ? (det.code.code?.trim() || "U") : "T";
            }

            const displayName =
                det.code?.name?.trim() ||
                det.code?.code?.trim() ||
                det.monitoring.status.site_name;

            return { ...det, displayCode, displayName };
        })
        .sort((a, b) => {
            // If one is "T" and the other isn't, "T" goes to the end
            if (a.displayCode === "T" && b.displayCode !== "T") return 1;
            if (a.displayCode !== "T" && b.displayCode === "T") return -1;

            // If both are "T" or both are not "T", sort by ID
            const idA = a.code?.id ? parseInt(a.code.id) : Infinity;
            const idB = b.code?.id ? parseInt(b.code.id) : Infinity;

            return idA - idB;
        });

    // 2️⃣ Pagination
    const totalPages = detectorsPerPage > 0 ? Math.ceil(processedAndSortedDetectors.length / detectorsPerPage) : 1;
    const startIndex = (currentPage - 1) * detectorsPerPage;
    const currentDetectors = processedAndSortedDetectors.slice(startIndex, startIndex + detectorsPerPage);

    useEffect(() => {
        const calculateDetectorsPerPage = () => {
            if (!gridRef.current) return;

            // Read the actual number of columns the browser created
            const computedStyle = getComputedStyle(gridRef.current);
            const columnCount = computedStyle
                .getPropertyValue('grid-template-columns')
                .trim()
                .split(' ')
                .length;

            const perPage = Math.max(1, columnCount) * 4; // 4 rows
            setDetectorsPerPage(perPage);
        };

        calculateDetectorsPerPage();

        const timeout = setTimeout(calculateDetectorsPerPage, 100);

        window.addEventListener("resize", calculateDetectorsPerPage);
        return () => {
            window.removeEventListener("resize", calculateDetectorsPerPage);
            clearTimeout(timeout);
        };
    }, [currentDetectors.length]);

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    // -------------------------------------------------- Most Recent Files --------------------------------------------------
    const [fileDataUSA, setfileDataUSA] = useState<AllServerFilesObject | null>(null);
    const [fileDataLastModifiedUSA, setFileDataLastModifiedUSA] = useState<string | null>(null);
    const [fileSearchTerm, setFileSearchTerm] = useState('');

    const fetchFileData = async (trigger?: string, source?: any) => {
        try {
            const url = new URL(
                '/apidet/fnnbackend/fnnMostRecentFilesView',
                window.location.origin
            );

            if (trigger) {
                url.searchParams.append('trigger', trigger);
            }

            url.searchParams.append('origin', source);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: "same-origin",
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: NewServerFilesFormat = await response.json();

            if (source === 'dataUSA') {
                setfileDataUSA(data.data);
                setFileDataLastModifiedUSA(data.last_triggered);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchFileData(undefined, 'dataUSA');

        const interval = setInterval(() => {
            fetchFileData(undefined, 'dataUSA');
        }, 50000);

        return () => clearInterval(interval);
    }, []);

    const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);

    const handleFilePathSelected = (
        filepath: string,
        source: 'dataUSA'
    ) => {
        setSelectedFilePath(filepath);
        const encodedPath = encodeURIComponent(filepath);
        window.open(
            `/fnnbackend/plot?filepath=${encodedPath}&source=${source}`,
            '_blank'
        );
    };

    // ------------------------------------------- System Configuration -- FNN Tool -------------------------------------------
    const [commandResults, setCommandResults] = useState<{ [key: string]: string }>({});
    const [threshold, setThreshold] = useState('');
    const [memory, setMemory] = useState('');

    function displayNotification(successOrFail: boolean, message: string) {
        if (successOrFail) {
            toast.success(message, {
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
        else {
            toast.error(message, {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: '#e7ecf1ff',
                    color: 'black',
                    fontSize: '16px',
                }
            });
        }
    }

    const displayCommand = async (detectorIP: string, command: string, value = '', skipNotification = false) => {
        try {
            const url = new URL(`/apidet/fnnbackend/fnnToolView/`, window.location.origin);
            url.searchParams.append("ip", detectorIP);
            url.searchParams.append("command", command);
            url.searchParams.append("origin", 'dataUSA');

            if (command.startsWith("SET")) {
                url.searchParams.append("value", value);
            }

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "same-origin",
            });

            if (!response.ok) throw new Error("Failed to fetch data");

            const data = await response.json();
            const formattedResult = data.result.includes(":") ? data.result.split(":")[1].trim() : data.result;

            setCommandResults(prev => ({ ...prev, [command]: formattedResult || "No data received" }));

            if (command === "GET_THR") setThreshold(formattedResult || '');
            if (command === "GET_MEM") setMemory(formattedResult || '');

        } catch (error) {
            console.error("Error fetching command:", error);
            displayNotification(false, "There was an issue completing the request.");
        }
    };

    const handleSetThreshold = (detectorIP: string) => {
        if (!/^\d+$/.test(threshold.trim())) {
            displayNotification(false, "Threshold must be a whole number between 5 and 2047.");
            return;
        }

        const thresholdValue = parseInt(threshold.trim(), 10);

        if (thresholdValue < 5 || thresholdValue > 2047) {
            displayNotification(false, "Threshold must be an integer between 5 and 2047.");
            return;
        }

        displayCommand(detectorIP, "SET_THR", threshold);
        setTimeout(() => displayCommand(detectorIP, "GET_THR", '', true), 500);
    };

    const handleSetMemory = (detectorIP: string) => {
        if (!/^\d+$/.test(memory.trim())) {
            displayNotification(false, "Memory must be a whole number between 1 and 99.");
            return;
        }

        const memoryValue = parseInt(memory.trim(), 10);

        if (memoryValue < 1 || memoryValue > 99) {
            displayNotification(false, "Memory must be an integer between 1 and 99.");
            return;
        }

        displayCommand(detectorIP, "SET_MEM", memory);
        setTimeout(() => displayCommand(detectorIP, "GET_MEM", '', true), 500);
    };

    function parseEncodedLocation(encoded: string) {
        if (!encoded) return null;

        const parts = encoded.split(",");
        if (parts.length !== 2) return null;

        const latRaw = parseInt(parts[0]);
        const lonRaw = parseInt(parts[1]);

        const latSign = (latRaw >> 31) & 0x1;
        const latDeg = (latRaw >> 23) & 0xFF;
        const latMin = (latRaw >> 17) & 0x3F;
        const latFrac = latRaw & 0x1FFFF;
        const lat = (latDeg + (latMin + latFrac / 131072) / 60) * (latSign ? 1 : -1);

        const lonSign = (lonRaw >> 31) & 0x1;
        const lonDeg = (lonRaw >> 23) & 0xFF;
        const lonMin = (lonRaw >> 17) & 0x3F;
        const lonFrac = lonRaw & 0x1FFFF;
        const lon = (lonDeg + (lonMin + lonFrac / 131072) / 60) * (lonSign ? -1 : 1);

        return {
            lat: parseFloat(lat.toFixed(5)),
            lon: parseFloat(lon.toFixed(5)),
        };
    }

    // Deleting the detector from detector.json
    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin", // include cookies in the request
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken; // Return the CSRF token
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const handleDeleteDetector = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
            toast.error("Failed to get CSRF token");
            return;
        }

        if (!selectedDetector?.monitoring?.mac) {
            toast.error("No detector selected");
            return;
        }

        try {
            const response = await fetch(
                "/portal/api/detectors/delete/",
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken
                    },
                    credentials: "same-origin",
                    body: JSON.stringify({
                        mac: selectedDetector.monitoring.mac,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || data.detail || "Delete failed");
            }

            // Success!
            toast.success(`Detector ${selectedDetector.monitoring.mac} deleted successfully`, {
                autoClose: 3000,
            });

            // Close the popup and zoom out the map
            const marker = markerRefs.current[selectedDetector.monitoring.mac];
            if (marker) {
                marker.closePopup();
            }
            mapRef.current?.flyTo([0, 0], 2, { duration: 1.2 });

            // Clear selection
            setSelectedDetector(null);
            setSearchTerm("");

            // Refresh the detector data
            await refreshDetectors();

        } catch (err: any) {
            console.error("Delete detector error:", err);
            toast.error(err.message || "Failed to delete detector", {
                autoClose: 4000,
            });
        }
    };

    // Delete Popup
    const [showDeleteDetectorPopup, setShowDeleteDetectorPopup] = useState(false);

    // Format the last time the detector was fetched
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);



    const formatTimeAgo = (date: Date | null) => {
        if (!date) return "—";

        const rawDiff = (now - date.getTime()) / 1000;

        const diff = Math.max(
            0,
            Math.floor((Date.now() - date.getTime()) / 1000)
        );


        if (diff < 60) return `${diff} seconds ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;

        return `${Math.floor(diff / 86400)} days ago`;
    };





    return (
        <div className='customer-directory-body'>
            <ToastContainer />
            <TeamHeader title="Hardware" />
            <h1 className='cutomer-title'>Detector Management</h1>

            {/* Search + Filter */}
            <div className='search-for-hardware'>
                <div className='customer-search-and-sort'>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className="customer-search-wrapper">
                            <FaMagnifyingGlass className="search-icon" />
                            <input
                                className="customer-search"
                                type="text"
                                name="Search_Field"
                                placeholder="Search by site, MAC, or IP..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <FilterDropdown
                            dropdownClassName="detector-status-dropdown"
                            sideClass="detector-side-drop"
                            initialSelected={{ test: [testFilter] }} // <-- initial radio value
                            resetValue={{ test: ["include"] }} // always reset to "include"
                            sections={[
                                {
                                    key: "regions",
                                    title: "Region",
                                    options: regions.filter(r => r !== "All"),
                                    type: "checkbox",
                                },
                                {
                                    key: "status",
                                    title: "Status",
                                    options: ["Online", "Offline"],
                                    type: "checkbox",
                                },
                                {
                                    key: "test",
                                    title: "Test",
                                    options: ["include", "exclude"],
                                    type: "radio",
                                    formatLabel: (option) =>
                                        option.charAt(0).toUpperCase() + option.slice(1),
                                },
                            ]}
                            onChange={(filters) => {
                                setSelectedRegions(
                                    filters.regions && filters.regions.length > 0 ? filters.regions : ["All"]
                                );
                                setStatusFilters(filters.status || []);
                                setTestFilter(
                                    (filters.test && filters.test[0] ? filters.test[0] : "include") as
                                    | "include"
                                    | "exclude"
                                );
                            }}
                        />






                    </div>

                    <div className='detector-last-updated-text'>
                        <p><strong>Last Updated</strong></p>
                        <p>{formatTimeAgo(lastFetched)}</p>
                    </div>



                </div>

                <div className="hardware2-legend">
                    <div className="hardware2-legend-item">
                        <p><strong>U</strong> - Unasigned {showInfoTooltip("Indicate detectors whose code has not been assigned yet.")}</p>
                    </div>

                    <div className="hardware2-legend-item">
                        <p><strong>T</strong> - Test {showInfoTooltip("Indicate detectors currently used for testing.")}</p>
                    </div>

                    {[
                        { color: '#3FF447', label: 'Online' },
                        { color: '#a6a6a6', label: 'Offline' },
                        { color: '#FFD93D', label: '80% Memory' },
                        { color: '#FF4C4C', label: '85% Memory' },
                    ].map(({ color, label }) => (
                        <div key={label} className="hardware2-legend-item">
                            <span className="hardware2-legend-color" style={{ backgroundColor: color }}></span>
                            <p>{label}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Detector Map and Information */}
            <div className='hardware2-detec-wrapper-new-with-map'>
                <div className="hardware2-detec-info-wrapper-new-for-map">
                    <MapContainer
                        center={[0.00, 0.00]}
                        zoom={2}
                        style={{ width: "100%", height: "100%" }}
                        ref={mapRef}>

                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />

                        {processedAndSortedDetectors.map((detector) => {
                            const lat = Number(detector.monitoring.status.gps_lat);
                            const lng = Number(detector.monitoring.status.gps_long);

                            if (!lat || !lng) return null;

                            return (
                                <Marker
                                    key={detector.monitoring.mac}
                                    position={[lat, lng]}
                                    icon={getColorForMarker(detector.monitoring.online, Number(detector.monitoring.status.memory))}
                                    ref={el => (markerRefs.current[detector.monitoring.mac] = el)}
                                    eventHandlers={{
                                        click: () => {
                                            handleSelectDetector(detector);
                                        },
                                    }}
                                >
                                    <Popup>
                                        <div className='new-popup-content'>
                                            {detector.code?.code && <p style={{ paddingTop: '1rem' }}><strong>Code:</strong> {detector.code?.code}</p>}
                                            <p style={detector.code?.code ? undefined : { marginTop: "1rem" } }>
                                                <strong>Detector:</strong>{" "}
                                                <span style={{ color: '#28aee1' }}>
                                                    {detector.displayName || detector.monitoring.mac}
                                                </span>
                                            </p>
                                            <p><strong>MAC:</strong> {detector.monitoring.mac}</p>
                                            <p>
                                                <strong>Status:</strong>{" "}
                                                <span style={{ color: detector.monitoring.online ? 'green' : 'red' }}>
                                                    {detector.monitoring.online ? 'Online' : 'Offline'}
                                                </span>
                                            </p>
                                            <p><strong>Latitude:</strong> {lat}</p>
                                            <p><strong>Longitude:</strong> {lng}</p>
                                            <div className='pop-changeableValues'>
                                                <strong> Memory: </strong>
                                                <span style={{ color: getColorForMemory(Number(detector.monitoring.status.memory)) }}>
                                                    {detector.monitoring.status.memory}
                                                </span>
                                            </div>
                                            <div className='pop-changeableValues'>
                                                <strong> GPS Fix: </strong>
                                                <span style={{ color: getColorForGPSFix(detector.monitoring.status.gps_fix) }}>
                                                    {detector.monitoring.status.gps_fix ? "Yes" : "No"}
                                                </span>
                                            </div>
                                            <div className='google-maps-button-div'
                                                onClick={() =>
                                                    window.open(
                                                        `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                                                        "_blank",
                                                        "noopener,noreferrer"
                                                    )
                                                }
                                            >
                                                <img src={googleMapsLogo} alt='gmaps' />
                                                <p>Google Maps</p>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        })}
                    </MapContainer>
                </div>

                {/* Detector Information */}
                <div className='hardware2-detec-info-wrapper-new'>
                    {selectedDetector ? (
                        <>
                            <div className='hardware2-title-and-delete'>
                                <h1>
                                    {selectedDetector.code?.name || 'Unknown Site'} - {selectedDetector.monitoring.mac}{" "}
                                </h1>

                                {activeTab === "hardware" && (() => {
                                    // Check if there are hardware notes for this detector
                                    const detectorIdentifier = selectedDetector.code?.id && selectedDetector.code?.code
                                        ? `${selectedDetector.code.id}_${selectedDetector.code.code}`
                                        : null;

                                    const hasHardwareNotes = detectorIdentifier && hardwareNotes.some(
                                        note => note.note_related_detector === detectorIdentifier
                                    );

                                    if (!hasHardwareNotes) return null;

                                    return (

                                        <Tooltip
                                            message="Detector has one or more hardware modifications. Click to check details."
                                            showTooltip={true}
                                            extraClassName="tooltip-no-arrow-left"
                                        >
                                            <BsExclamationSquare className='hardware2-history-icon'
                                                onClick={() => {
                                                    if (selectedDetector.code?.id && selectedDetector.code?.code) {
                                                        const detKey = `${selectedDetector.code.id}_${selectedDetector.code.code}`;
                                                        const url = `/hardware/history?search=${encodeURIComponent(detKey)}`;
                                                        window.open(url, "_blank");
                                                    }
                                                }} />
                                        </Tooltip>

                                    );
                                })()}

                            </div>

                            <div className='hardware2-detec-outer-sections'>

                                <div className='hardware2-detec-info-sections'>
                                    <button
                                        className={activeTab === "details" ? "active" : ""}
                                        onClick={() => setActiveTab("details")}
                                    >
                                        System Details
                                    </button>
                                    <button
                                        className={activeTab === "hardware" ? "active" : ""}
                                        onClick={() => setActiveTab("hardware")}
                                    >
                                        Hardware Information
                                    </button>

                                    <Tooltip
                                        message="Offline detectors cannot access System Configuration"
                                        showTooltip={!selectedDetector?.monitoring.online}
                                    >
                                        <button
                                            className={activeTab === "config" ? "active" : ""}
                                            disabled={!selectedDetector?.monitoring.online}
                                            onClick={() => {
                                                if (selectedDetector?.monitoring.online && selectedDetector?.monitoring.ip) {
                                                    setActiveTab("config");
                                                    displayCommand(selectedDetector.monitoring.ip, "GET_LOC");
                                                    displayCommand(selectedDetector.monitoring.ip, "GET_DAC");
                                                    displayCommand(selectedDetector.monitoring.ip, "GET_THR");
                                                    displayCommand(selectedDetector.monitoring.ip, "GET_MEM");
                                                }
                                            }}
                                        >
                                            System Configuration
                                        </button>
                                    </Tooltip>
                                </div>

                                <div
                                    className='hh-delete-btn hardware2-delete-button'
                                    onClick={() => setShowDeleteDetectorPopup(true)}

                                >
                                    Delete
                                </div>
                            </div>

                            {activeTab === "hardware" && (
                                detectorVersionInfo ? (
                                    <div className='hardware2-detec-info-block'>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div className='hardware2-detec-info-group'>
                                                <strong><p>Assembled On:</p></strong>
                                                <p>{new Date(detectorVersionInfo.built_at).toLocaleString()}</p>
                                            </div>
                                            <u
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    if (selectedDetector.code?.id && selectedDetector.code?.code) {
                                                        const detKey = `${selectedDetector.code.id}_${selectedDetector.code.code}`;
                                                        const url = `/hardware/history?search=${encodeURIComponent(detKey)}`;
                                                        window.open(url, "_blank");
                                                    }
                                                }}

                                            >
                                                View Hardware History
                                            </u>

                                        </div>

                                        <div className='hardware2-detec-info-group'>
                                            <strong><p>Assembled By:</p></strong>
                                            <p>{getFnnUserName(detectorVersionInfo.assembled_by)}</p>
                                        </div>

                                        <div className='hardware2-detec-info-group'>
                                            <strong><p>Detector Version:</p></strong>
                                            <p>
                                                {(() => {
                                                    const detectorObj = detectors.find(
                                                        (d) => d.id === selectedDetector.built?.detector
                                                    );
                                                    return detectorObj ? detectorObj.version : "N/A";
                                                })()}
                                            </p>
                                        </div>

                                        <strong>
                                            <p>
                                                Sub-Assemblies{" "}
                                                {showInfoTooltip(
                                                    "Click the Version to view the sub-assembly's Bill of Materials (BOM)."
                                                )}
                                            </p>
                                        </strong>

                                        <div className='hardware2-detec-sub-grid'>
                                            {detectorVersionInfo.subassemblies.map((s, i) => {
                                                const [code, name] = s.split(':').map(str => str.trim());

                                                return (
                                                    <div key={i} className='hardware2-detec-sub'>
                                                        <strong style={{ color: '#28aee1' }}>
                                                            <p>{code}</p>
                                                        </strong>
                                                        <div className="hardware2-detec-version-inline">
                                                            <u
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => {
                                                                    const url = `/inventory?openBOM=true&sub=${encodeURIComponent(name)}`;
                                                                    window.open(url, "_blank");
                                                                }}
                                                            >
                                                                {name}
                                                            </u>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className='hardware2-detec-info-button-group'>
                                            <button
                                                className='hardware2-detec-info-recent-files-div-button'
                                                onClick={() => {
                                                    if (selectedDetector.built?.mac_address) {
                                                        const url = `/inventory?openAssembly=true&mac=${encodeURIComponent(selectedDetector.built.mac_address)}&status=installed`;
                                                        window.open(url, "_blank");
                                                    }
                                                }}
                                            >
                                                View Detector Assembly
                                            </button>

                                            <button
                                                className='hardware2-detec-info-recent-files-div-button'
                                                onClick={() => {
                                                    if (selectedDetector.built?.detector) {
                                                        const url = `/inventory?openBOM=true&det=${selectedDetector.built.detector}`;
                                                        window.open(url, "_blank");
                                                    }
                                                }}
                                            >
                                                View Detector BOM
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='hardware2-detec-info-block'>
                                        <p>No hardware information available for this detector yet.</p>
                                    </div>
                                )
                            )}

                            {activeTab === "details" && (
                                <div className='hardware2-detec-info-block hardware2-ext'>
                                    <div className='details-block'>
                                        <p onClick={() =>
                                            window.open(
                                                `https://www.google.com/maps/search/?api=1&query=${selectedDetector.monitoring.status.gps_lat},${selectedDetector.monitoring.status.gps_long}`,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        ><strong>Site:</strong> <span className='to-link-class2'>{selectedDetector.code?.name ? selectedDetector.code?.name : selectedDetector.monitoring.status.site_name} <FaMapMarkerAlt style={{ display: 'inline' }} /></span></p>
                                        <p><strong>Code:</strong> {selectedDetector.code?.code ? selectedDetector.code?.code : "N/A"}</p>
                                        <p><strong>MAC:</strong> {selectedDetector.monitoring.mac}</p>
                                        <p><strong>IP:</strong> {selectedDetector.monitoring.ip}</p>
                                        <p>
                                            <strong>Last Active:</strong>{" "}
                                            {selectedDetector.monitoring.last_active
                                                ? new Date(selectedDetector.monitoring.last_active)
                                                    .toISOString()
                                                    .replace("T", " ")
                                                    .replace(/\.\d+Z$/, " UTC")
                                                    .replace(/-/g, "/")
                                                : "N/A"}
                                        </p>
                                        <p><strong>Up Since:</strong> {selectedDetector.monitoring.status.up_since}</p>
                                        <p><strong>GPS Fix:</strong> {selectedDetector.monitoring.status.gps_fix ? 'Yes' : 'No'}</p>
                                        <p><strong>Latitude:</strong> {selectedDetector.monitoring.status.gps_lat}</p>
                                        <p><strong>Longitude:</strong> {selectedDetector.monitoring.status.gps_long}</p>
                                        <p><strong>Firmware Version:</strong> {selectedDetector.monitoring.status.fw_version}</p>
                                        <p><strong>Memory Usage:</strong> {selectedDetector.monitoring.status.memory}</p>
                                        <p><strong>Threshold:</strong> {selectedDetector.monitoring.status.thr}</p>
                                        <p><strong>Linux:</strong> {selectedDetector.monitoring.status.linux ? 'Yes' : 'No'}</p>
                                        <p><strong>Test Mode:</strong> {selectedDetector.monitoring.status.test_mode ? 'Enabled' : 'Disabled'}</p>
                                        <p><strong>SIM Card:</strong> {selectedDetector.code?.sim_card}</p>
                                        <p><strong>DACQ algorithm:</strong> {(selectedDetector.monitoring.status.dacq_algo && selectedDetector.monitoring.status.dacq_algo == true) ? "Online" : "Offline"} </p>
                                        <p><strong>Peak algorithm</strong> {(selectedDetector.monitoring.status.peak_algo && selectedDetector.monitoring.status.peak_algo == true) ? "Online" : "Offline"}</p>
                                        <p><strong>File-transfer algorithm:</strong> {(selectedDetector.monitoring.status.file_algo && selectedDetector.monitoring.status.file_algo == true) ? "Online" : "Offline"}</p>
                                        <p><strong>CSV-transfer algorithm:</strong> {(selectedDetector.monitoring.status.csv_algo && selectedDetector.monitoring.status.csv_algo == true) ? "Online" : "Offline"}</p>
                                        <p><strong>% of time online last 24 hrs:</strong> TBD</p>
                                        <p><strong className='clickable-details-item' onClick={() => setShowFilePopup(true)} >Files transferred/hour:</strong> TBD</p>
                                    </div>
                                </div>
                            )}

                            {activeTab === "config" && (
                                selectedDetector.monitoring.online ? (
                                    <div className='hardware2-detec-info-block'>
                                        <div className='hardware2-detec-sub-grid'>
                                            <div className='hardware2-detec-sub'>
                                                <strong style={{ color: '#28aee1' }}>
                                                    <p>Location</p>
                                                </strong>
                                                <div className="hardware2-detec-version-inline">
                                                    {(() => {
                                                        const loc = parseEncodedLocation(commandResults["GET_LOC"]);
                                                        return loc ? (
                                                            <>
                                                                <p>LAT: {loc.lat.toFixed(5)}</p>
                                                                <p>LNG: {loc.lon.toFixed(5)}</p>
                                                            </>
                                                        ) : (
                                                            <p>{commandResults["GET_LOC"] || "—"}</p>
                                                        );
                                                    })()}
                                                </div>
                                            </div>

                                            <div className='hardware2-detec-sub'>
                                                <strong style={{ color: '#28aee1' }}>
                                                    <p>DAC</p>
                                                </strong>
                                                <div className="hardware2-detec-version-inline">
                                                    {commandResults["GET_DAC"] ? (
                                                        commandResults["GET_DAC"].split(",").map((value, index) => (
                                                            <React.Fragment key={index}>
                                                                {value.trim()}
                                                                <br />
                                                            </React.Fragment>
                                                        ))
                                                    ) : (
                                                        <p>—</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className='hardware2-detec-sub'>
                                                <strong style={{ color: '#28aee1' }}>
                                                    <p>Threshold</p>
                                                </strong>
                                                <div className="hardware2-detec-version-inline">
                                                    <p>{commandResults["GET_THR"] || "—"}</p>
                                                </div>
                                            </div>

                                            <div className='hardware2-detec-sub'>
                                                <strong style={{ color: '#28aee1' }}>
                                                    <p>Memory</p>
                                                </strong>
                                                <div className="hardware2-detec-version-inline">
                                                    <p>
                                                        {commandResults["GET_MEM"]
                                                            ? `${commandResults["GET_MEM"]}%`
                                                            : "—"}
                                                    </p>
                                                </div>
                                            </div>

                                        </div>

                                        <div className='hardware2-detec-sub-grid hardware2-detec-sets-wrapper'>
                                            <div className='hardware2-detec-sets'>
                                                <div
                                                    className='hardware2-detec-sub-scope confirm-btn'
                                                    onClick={() => {
                                                        if (selectedDetector?.monitoring.online && selectedDetector?.monitoring.ip) {
                                                            displayCommand(selectedDetector.monitoring.ip, "SCOPE");
                                                        }
                                                    }}
                                                >
                                                    <AiOutlineAim />
                                                    <strong>Scope</strong>
                                                </div>


                                                <div className="hardware2-detec-version-inline">
                                                    {commandResults["SCOPE"]
                                                        ? (() => {
                                                            const words = commandResults["SCOPE"].split(" ");
                                                            const mid = Math.floor(words.length / 2);
                                                            const firstHalf = words.slice(0, mid).join(" ");
                                                            const secondHalf = words.slice(mid).join(" ");
                                                            return (
                                                                <p>
                                                                    {firstHalf}
                                                                    <br />
                                                                    {secondHalf}
                                                                </p>
                                                            );
                                                        })()
                                                        : "Click to Scope"}
                                                </div>
                                            </div>

                                            <div className='hardware2-detec-sets'>
                                                <label>Set Threshold</label>
                                                <div className='inner-tool-label-and-input'>
                                                    <input
                                                        type="text"
                                                        name="thr_inp"
                                                        placeholder="Enter Value"
                                                        value={threshold}
                                                        onChange={(e) => setThreshold(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && selectedDetector?.monitoring.ip) {
                                                                handleSetThreshold(selectedDetector.monitoring.ip);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className='hardware2-detec-sets' style={{ justifySelf: 'flex-start' }}>
                                                <label>Set Memory %</label>
                                                <div className='inner-tool-label-and-input'>
                                                    <input
                                                        type="text"
                                                        name="mem_inp"
                                                        placeholder="Enter Value"
                                                        value={memory}
                                                        onChange={(e) => setMemory(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && selectedDetector?.monitoring.ip) {
                                                                handleSetMemory(selectedDetector.monitoring.ip);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>


                                        </div>
                                    </div>
                                ) : (
                                    <div className='hardware2-detec-info-block'>
                                        <p>Offline Detectors cannot make requests.</p>
                                    </div>
                                )
                            )}
                        </>
                    ) : (
                        <p style={{ textAlign: 'center', marginTop: '1rem' }}>Select a detector to view details.</p>
                    )}
                </div>
            </div>

            {/* Detector Grid */}
            <div className='hardware2-detec-grid-wrapper'>
                <div ref={gridRef} className='hardware2-detec-grid'>
                    {currentDetectors.length > 0 ? (
                        currentDetectors.map((det, index) => {
                            const isSelected = selectedDetector?.monitoring.mac === det.monitoring.mac;

                            return (
                                <div
                                    key={index}
                                    className={`hardware2-detec-square ${isSelected ? "selected" : selectedDetector ? "dimmed" : ""}`}
                                    style={{
                                        // background with 15% opacity
                                        background: `${getDetectorColor(det)}26`, // 26 hex = ~15% opacity
                                        border: `1px solid ${getDetectorColor(det)}`, // solid border using same color
                                        cursor: "pointer",
                                        "--detector-color": getDetectorColor(det),
                                        "--detector-color-dark": darkenColor(getDetectorColor(det), 20),
                                    } as React.CSSProperties}
                                    title={`${det.displayName} | ${det.monitoring.ip} | Mem: ${det.monitoring.status.memory}`}
                                    onClick={() => {
                                        if (selectedDetector?.monitoring.mac === det.monitoring.mac) {
                                            setSelectedDetector(null);
                                            const marker = markerRefs.current[det.monitoring.mac];
                                            if (marker) marker.closePopup();
                                            mapRef.current?.flyTo([0, 0], 2, { duration: 1.2 });
                                        } else {
                                            handleSelectDetector(det);
                                            openPopup(det.monitoring.mac);
                                        }
                                    }}
                                >
                                    <FaCircle
                                        style={{
                                            color: getDetectorColor(det), // use same color as background/border
                                        }}
                                    />
                                    <p>{det.code?.id ?? "-"} {det.displayCode}</p>

                                </div>
                            );
                        })
                    ) : (
                        <p>No detectors found.</p>
                    )}
                </div>
            </div>

            {/* Tab Control */}
            <div className='hardware2-detec-tab-control'>
                <IoIosArrowBack
                    onClick={handlePrevPage}
                    style={{ opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "default" : "pointer" }}
                />

                <div className='hardware2-detec-tab-numbers'>
                    {[...Array(totalPages)].map((_, i) => (
                        <p
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            style={{
                                fontWeight: currentPage === i + 1 ? "bold" : "normal",
                                cursor: "pointer",
                                textDecoration: currentPage === i + 1 ? "underline" : "none",
                                color: currentPage === i + 1 ? "#28aee1" : "black"
                            }}
                        >
                            {i + 1}
                        </p>
                    ))}
                </div>

                <IoIosArrowForward
                    onClick={handleNextPage}
                    style={{ opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "default" : "pointer" }}
                />
            </div>

            {showFilePopup && (
                <div className='hardware2-popup-overlay'>
                    <div className='hardware2-popup-content'>
                        <div className='hardware2-file-title-and-search'>
                            <h1>{selectedDetector?.code?.code ? selectedDetector?.code?.code : selectedDetector?.code?.name}</h1>

                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <div className="search-box">
                                    <IoSearchSharp className="hardware2-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="hardware2-file-input"
                                        value={fileSearchTerm}
                                        onChange={(e) => setFileSearchTerm(e.target.value)}
                                    />
                                </div>

                                <button
                                    onClick={() => (setShowFilePopup(false), setFileSearchTerm(''))}>
                                    <IoClose />
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="hardware2-file-table-wrapper">
                            <table className='hardware2-file-strip'>
                                <thead>
                                    <tr>
                                        <th>File Name</th>
                                        <th>Timestamp</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        if (!fileDataUSA || !selectedDetector?.monitoring.status?.site_name) {
                                            return (
                                                <tr>
                                                    <td colSpan={3} style={{ textAlign: 'center' }}>
                                                        No file data available.
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        const siteKey = Object.keys(fileDataUSA).find(site =>
                                            selectedDetector.code?.name.toLowerCase().includes(site.toLowerCase())
                                        );

                                        let files = siteKey ? fileDataUSA[siteKey] : [];

                                        if (fileSearchTerm.trim() !== '') {
                                            const term = fileSearchTerm.toLowerCase();
                                            files = files.filter(file => file.filename.toLowerCase().includes(term));
                                        }

                                        if (!files?.length) {
                                            return (
                                                <tr>
                                                    <td colSpan={3} style={{ textAlign: 'center' }}>
                                                        No recent files found for this site.
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        return files.map((file, index) => {
                                            const isBin = file.filename.toLowerCase().endsWith('.bin');

                                            return (
                                                <tr
                                                    key={index}
                                                    style={{ cursor: isBin ? 'pointer' : 'not-allowed' }}
                                                    className={isBin ? 'clickable-file-row' : 'non-clickable-file-row'}
                                                >
                                                    <td
                                                        style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
                                                        onClick={isBin ? () => handleFilePathSelected(file.filepath, 'dataUSA') : undefined}
                                                    >
                                                        <strong>{index + 1}.</strong>
                                                        <span>{file.filename}</span>
                                                    </td>

                                                    <td
                                                        onClick={isBin ? () => handleFilePathSelected(file.filepath, 'dataUSA') : undefined}
                                                    >
                                                        {file.timestamp}
                                                    </td>

                                                    <MdFileDownload
                                                        className='hardware2-download-icon'
                                                        title="Download file"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();

                                                            try {
                                                                const response = await fetch(
                                                                    `/apidet/fnnbackend/getFileToPlot?origin=dataUSA&path=${encodeURIComponent(file.filepath)}`,
                                                                    { method: 'GET', credentials: "same-origin" }
                                                                );

                                                                if (!response.ok) throw new Error(`HTTP error ${response.status}`);

                                                                const blob = await response.blob();
                                                                const url = window.URL.createObjectURL(blob);

                                                                const a = document.createElement('a');
                                                                a.href = url;
                                                                a.download = file.filename;
                                                                document.body.appendChild(a);
                                                                a.click();
                                                                a.remove();

                                                                window.URL.revokeObjectURL(url);
                                                            } catch (err) {
                                                                console.error('Download failed:', err);
                                                            }
                                                        }}
                                                    />
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Detector Popup */}
            {showDeleteDetectorPopup && selectedDetector && (
                <div className="add-item-popup-overlay">
                    <div className="add-item-popup-content hh-del-pop">
                        <div className="hh-delete-inner-pop" style={{ gap: "2rem" }}>

                            <div className="hh-delete-text-and-icon">
                                <PiPlugsFill className="hardware2-delete-icon" />

                                <div>
                                    <h3>Delete Detector</h3>

                                    <p>
                                        Are you sure you want to permanently delete this detector?
                                    </p>

                                    <p style={{ marginTop: "1rem" }}>
                                        <strong>MAC:</strong> {selectedDetector.monitoring.mac}<br />
                                        <strong>Site:</strong>{" "}
                                        {selectedDetector.code?.name ||
                                            selectedDetector.monitoring.status.site_name || 'Unknown Site'}<br />
                                        <strong>IP:</strong> {selectedDetector.monitoring.ip}
                                    </p>

                                    <p style={{ marginTop: "1rem", color: "#d92d20" }}>
                                        This action cannot be undone.
                                    </p>
                                </div>
                            </div>

                            <div className="hh-duo hh-actions">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowDeleteDetectorPopup(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="hh-delete-btn"
                                    onClick={async () => {
                                        setShowDeleteDetectorPopup(false);
                                        await handleDeleteDetector();
                                    }}
                                >
                                    Delete Detector
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default DetectorStatus;